import * as THREE from 'three';

export type DroneMesh = {
  group: THREE.Group;
  rotors: THREE.Object3D[];
  /** World-space origin for the survey scanner apex (camera lens). */
  cameraLens: THREE.Object3D;
};

/**
 * Low-poly agricultural survey quadcopter.
 * Origin at body center; arm tips ~0.55 from center.
 */
export function createDrone(): DroneMesh {
  const group = new THREE.Group();
  const rotors: THREE.Object3D[] = [];

  const shellMat = new THREE.MeshStandardMaterial({
    color: 0x6b7280,
    flatShading: true,
    roughness: 0.55,
  });
  const darkMat = new THREE.MeshStandardMaterial({
    color: 0x374151,
    flatShading: true,
    roughness: 0.7,
  });
  const chassisMat = new THREE.MeshStandardMaterial({
    color: 0x1f2937,
    flatShading: true,
    roughness: 0.85,
  });
  const accentMat = new THREE.MeshStandardMaterial({
    color: 0x9ca3af,
    flatShading: true,
    roughness: 0.45,
    metalness: 0.25,
  });
  const glassMat = new THREE.MeshStandardMaterial({
    color: 0x64748b,
    flatShading: true,
    roughness: 0.2,
    metalness: 0.2,
  });
  const bladeMat = new THREE.MeshStandardMaterial({
    color: 0xd1d5db,
    flatShading: true,
    roughness: 0.5,
  });
  const ledMat = new THREE.MeshStandardMaterial({
    color: 0xfbbf24,
    emissive: 0xf59e0b,
    emissiveIntensity: 0.55,
    roughness: 0.4,
  });

  const add = (
    parent: THREE.Object3D,
    geo: THREE.BufferGeometry,
    mat: THREE.Material,
    x: number,
    y: number,
    z: number,
  ): THREE.Mesh => {
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(x, y, z);
    mesh.castShadow = true;
    parent.add(mesh);
    return mesh;
  };

  // --- Central airframe ---
  add(group, new THREE.BoxGeometry(0.48, 0.14, 0.48), shellMat, 0, 0.02, 0);
  add(group, new THREE.BoxGeometry(0.36, 0.1, 0.36), darkMat, 0, 0.12, 0);
  add(group, new THREE.BoxGeometry(0.28, 0.06, 0.28), accentMat, 0, 0.18, 0);
  // Belly plate
  add(group, new THREE.BoxGeometry(0.4, 0.05, 0.4), chassisMat, 0, -0.06, 0);

  // Nose fairing (reads forward = -Z)
  add(group, new THREE.BoxGeometry(0.22, 0.1, 0.16), darkMat, 0, -0.02, -0.28);
  add(group, new THREE.BoxGeometry(0.12, 0.08, 0.08), glassMat, 0, -0.04, -0.36);

  // Survey camera gimbal under belly — scanner apex attaches to cameraLens
  const cameraGimbal = new THREE.Group();
  cameraGimbal.position.set(0, -0.16, 0);
  group.add(cameraGimbal);
  add(cameraGimbal, new THREE.CylinderGeometry(0.05, 0.06, 0.06, 6), chassisMat, 0, 0.02, 0);
  add(cameraGimbal, new THREE.SphereGeometry(0.055, 8, 6), darkMat, 0, -0.02, 0);
  add(cameraGimbal, new THREE.CylinderGeometry(0.035, 0.04, 0.03, 8), glassMat, 0, -0.06, 0);

  const cameraLens = new THREE.Object3D();
  cameraLens.name = 'cameraLens';
  cameraLens.position.set(0, -0.08, 0);
  cameraGimbal.add(cameraLens);

  // Rear antenna
  add(group, new THREE.CylinderGeometry(0.015, 0.015, 0.22, 5), accentMat, 0.08, 0.28, 0.14);
  add(group, new THREE.BoxGeometry(0.04, 0.03, 0.04), darkMat, 0.08, 0.18, 0.14);

  // Status LEDs on top corners of the deck
  add(group, new THREE.BoxGeometry(0.04, 0.02, 0.04), ledMat, 0.14, 0.2, 0.14);
  add(group, new THREE.BoxGeometry(0.04, 0.02, 0.04), ledMat, -0.14, 0.2, 0.14);

  // Landing skids
  const skidY = -0.22;
  add(group, new THREE.BoxGeometry(0.06, 0.04, 0.55), chassisMat, 0.16, skidY, 0);
  add(group, new THREE.BoxGeometry(0.06, 0.04, 0.55), chassisMat, -0.16, skidY, 0);
  add(group, new THREE.BoxGeometry(0.04, 0.16, 0.04), darkMat, 0.16, -0.12, 0.16);
  add(group, new THREE.BoxGeometry(0.04, 0.16, 0.04), darkMat, 0.16, -0.12, -0.16);
  add(group, new THREE.BoxGeometry(0.04, 0.16, 0.04), darkMat, -0.16, -0.12, 0.16);
  add(group, new THREE.BoxGeometry(0.04, 0.16, 0.04), darkMat, -0.16, -0.12, -0.16);

  // --- Arms + motors + propellers ---
  const armLength = 0.42;
  const tipZ = 0.55;
  const armGeo = new THREE.BoxGeometry(0.07, 0.045, armLength);
  const motorGeo = new THREE.CylinderGeometry(0.07, 0.08, 0.08, 7);
  const hubGeo = new THREE.CylinderGeometry(0.03, 0.03, 0.04, 6);
  const bladeGeo = new THREE.BoxGeometry(0.52, 0.012, 0.055);

  const angles = [Math.PI / 4, (3 * Math.PI) / 4, -Math.PI / 4, -(3 * Math.PI) / 4];

  angles.forEach((angle, i) => {
    const armGroup = new THREE.Group();
    armGroup.rotation.y = angle;

    const arm = new THREE.Mesh(armGeo, darkMat);
    arm.position.set(0, 0.02, tipZ * 0.5);
    arm.castShadow = true;
    armGroup.add(arm);

    // Motor can
    const motor = new THREE.Mesh(motorGeo, chassisMat);
    motor.position.set(0, 0.06, tipZ);
    motor.castShadow = true;
    armGroup.add(motor);

    // Spinner hub
    add(armGroup, hubGeo, accentMat, 0, 0.12, tipZ);

    // Propeller: crossed blades in one mesh group (rotated as a unit)
    const rotor = new THREE.Group();
    rotor.position.set(0, 0.14, tipZ);

    const bladeA = new THREE.Mesh(bladeGeo, bladeMat);
    bladeA.castShadow = true;
    rotor.add(bladeA);

    const bladeB = new THREE.Mesh(bladeGeo, bladeMat);
    bladeB.rotation.y = Math.PI / 2;
    bladeB.castShadow = true;
    rotor.add(bladeB);

    // Slight stagger so front/rear props read differently
    if (i % 2 === 1) {
      rotor.rotation.y = Math.PI / 8;
    }

    armGroup.add(rotor);
    rotors.push(rotor);

    group.add(armGroup);
  });

  return { group, rotors, cameraLens };
}
